/**
 * EasyMode Action Capture Service — App-Wide Middleware
 *
 * Module-level singleton: ONE ActionBuffer + ONE session, shared across
 * the entire app.  Enabled on login, disabled on logout.
 *
 * Architecture:
 *   LoginScreen → setCaptureEnabled(true)
 *   persistState.tsx → captureMiddleware (checks flag, pushes to singleton)
 *   AppState → flush on blur/background
 *   Logout → setCaptureEnabled(false) + final flush
 *
 * Replaces the existing proactive action digest (5s timer) with
 * full Redux action batches for ML pipeline consumption.
 */

import { AppState, AppStateStatus } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { HOST, joinHostWithApiPath } from '@api/constant';

// ============================================================================
// Constants
// ============================================================================

const LOCAL_STORE_KEY = 'easy_mode_actions_buffer';
const LOCAL_SESSION_KEY = 'easy_mode_session_info';
const TTL_DAYS = 5;
const MAX_BUFFER_SIZE = 500; // Prevent memory bloat
const MAX_PAYLOAD_SIZE = 100 * 1024; // 100KB per fetch batch

// ============================================================================
// Types
// ============================================================================

interface CapturedAction {
  type: string;
  timestamp: string;
  payload: Record<string, unknown> | null;
  screen: string | null;
  duration: number | null;
}

interface SessionInfo {
  sessionId: string;
  userId: string;
  startedAt: string;
  actionCount: number;
}

// ============================================================================
// Module-Level Singleton (app-wide)
// ============================================================================

let captureEnabled = false;
let actionBuffer: ActionBuffer | null = null;
let sessionInfo: SessionInfo | null = null;
let appStateListener: (() => void) | null = null;

// ============================================================================
// Session Management
// ============================================================================

function generateSessionId(): string {
  return `easy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

async function getCurrentUserId(): Promise<string> {
  try {
    const user = await AsyncStorage.getItem('user');
    if (user) {
      const parsed = JSON.parse(user);
      if (parsed?._id || parsed?.id) {
        return parsed._id || parsed.id;
      }
    }
  } catch { /* ignore */ }
  return 'anonymous';
}

async function getOrCreateSession(): Promise<SessionInfo> {
  const userId = await getCurrentUserId();
  const now = new Date().toISOString();

  // Check for existing session (reuse if within TTL)
  try {
    const stored = await AsyncStorage.getItem(LOCAL_SESSION_KEY);
    if (stored) {
      const parsed: SessionInfo = JSON.parse(stored);
      const age = Date.now() - new Date(parsed.startedAt).getTime();
      if (age < TTL_DAYS * 24 * 60 * 60 * 1000) {
        return parsed;
      }
    }
  } catch { /* ignore, create new */ }

  return {
    sessionId: generateSessionId(),
    userId,
    startedAt: now,
    actionCount: 0
  };
}

// ============================================================================
// ActionBuffer — In-Memory Primary Store
// ============================================================================

class ActionBuffer {
  private actions: CapturedAction[] = [];
  private sessionId: SessionInfo | null = null;

  get size(): number { return this.actions.length; }

  push(action: Record<string, unknown>): void {
    this.actions.push({
      type: action.type as string || 'UNKNOWN',
      timestamp: new Date().toISOString(),
      payload: typeof action.payload === 'object' && action.payload !== null
        ? sanitizePayload(action.payload as Record<string, unknown>)
        : null,
      screen: null,
      duration: null
    });
    if (this.actions.length > MAX_BUFFER_SIZE) this.actions.shift();
  }

  flush(): CapturedAction[] {
    const buf = [...this.actions];
    this.actions = [];
    return buf;
  }

  setSession(info: SessionInfo): void { this.sessionId = info; }
  getSession(): SessionInfo | null { return this.sessionId; }
}

// ============================================================================
// Payload Sanitization
// ============================================================================

function sanitizePayload(payload: Record<string, unknown>): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null;

  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    // Strip sensitive fields
    if (['password', 'token', 'secret', 'apiKey', 'privateKey'].includes(key.toLowerCase())) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Limit depth (1 level)
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      sanitized[key] = truncateObject(value as Record<string, unknown>, 1);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.slice(0, 50).map(v =>
        typeof v === 'object' && v !== null ? truncateObject(v as Record<string, unknown>, 1) : v
      );
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

function truncateObject(obj: Record<string, unknown>, depth: number): Record<string, unknown> {
  if (depth <= 0 || !obj) return { _type: typeof obj, _truncated: true };
  const truncated: Record<string, unknown> = {};
  Object.keys(obj).slice(0, 20).forEach(key => {
    const value = obj[key];
    truncated[key] = typeof value === 'object' && value !== null
      ? truncateObject(value as Record<string, unknown>, depth - 1)
      : value;
  });
  return truncated;
}

// ============================================================================
// AsyncStorage Safety Net (crash recovery)
// ============================================================================

async function storeActionsLocally(actions: CapturedAction[]): Promise<void> {
  try {
    const existing = await AsyncStorage.getItem(LOCAL_STORE_KEY);
    let buffer: CapturedAction[] = existing ? JSON.parse(existing) : [];
    buffer = [...buffer, ...actions].slice(-MAX_BUFFER_SIZE);
    await AsyncStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(buffer));
  } catch (error) {
    console.warn('[EasyModeCapture] Local store failed:', error);
  }
}

async function retrieveLocalActions(): Promise<CapturedAction[]> {
  try {
    const stored = await AsyncStorage.getItem(LOCAL_STORE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

async function clearLocalBuffer(): Promise<void> {
  try {
    await AsyncStorage.removeItem(LOCAL_STORE_KEY);
  } catch { /* ignore */ }
}

// ============================================================================
// Sync to Backend
// ============================================================================

async function syncToBackend(
  sessionInfo: SessionInfo,
  actions: CapturedAction[]
): Promise<{ success: boolean; count: number }> {
  if (actions.length === 0) return { success: true, count: 0 };

  const payload = {
    userId: sessionInfo.userId,
    sessionId: sessionInfo.sessionId,
    actions: actions.map(a => ({
      type: a.type,
      timestamp: a.timestamp,
      payload: a.payload,
      screen: a.screen,
      duration: a.duration
    })),
    metadata: { source: 'easy_mode_app', version: '1.0.0' }
  };

  // Split if payload exceeds 100KB
  const serialized = JSON.stringify(payload);
  let batches: typeof payload[] = [payload];
  if (serialized.length > MAX_PAYLOAD_SIZE) {
    const half = Math.ceil(actions.length / 2);
    batches = [
      { ...payload, actions: payload.actions.slice(0, half) },
      { ...payload, actions: payload.actions.slice(half) }
    ];
  }

  let successCount = 0;
  let failedCount = 0;

  for (const batch of batches) {
    try {
      const response = await fetch(joinHostWithApiPath(HOST, '/api/easy-mode/ingest'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(batch)
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      successCount += batch.actions.length;
    } catch (error) {
      console.warn('[EasyModeCapture] Sync batch failed, kept in buffer:', error);
      failedCount += batch.actions.length;
    }
  }

  // Only clear buffer on full success
  if (successCount > 0 && failedCount === 0) {
    await clearLocalBuffer();
  }

  // Update session metadata
  const updatedSession: SessionInfo = {
    ...sessionInfo,
    actionCount: sessionInfo.actionCount + successCount
  };
  try {
    await AsyncStorage.setItem(LOCAL_SESSION_KEY, JSON.stringify(updatedSession));
  } catch { /* ignore */ }

  return { success: failedCount === 0, count: successCount };
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Enable capture — call on login success.
 * Creates the singleton buffer + session + AppState listener.
 */
export async function enableCapture(): Promise<void> {
  if (captureEnabled) return;
  captureEnabled = true;

  // Initialize singleton
  if (!actionBuffer) actionBuffer = new ActionBuffer();
  if (!sessionInfo) sessionInfo = await getOrCreateSession();

  // Save session to buffer
  actionBuffer.setSession(sessionInfo);

  // Save to AsyncStorage for crash recovery
  try {
    await AsyncStorage.setItem(
      LOCAL_STORE_KEY,
      JSON.stringify(sessionInfo)
    );
  } catch { /* ignore */ }

  // Setup AppState listener for visibility flush
  if (!appStateListener) {
    appStateListener = AppState.addEventListener('change', handleAppStateChange).remove;
  }

  console.log('[EasyModeCapture] Capture enabled');
}

/**
 * Disable capture + flush — call on logout.
 */
export async function disableCapture(): Promise<void> {
  if (!captureEnabled) return;
  captureEnabled = false;

  // Flush remaining buffer
  if (actionBuffer && actionBuffer.size > 0) {
    const buffer = actionBuffer.flush();
    if (sessionInfo) {
      syncToBackend(sessionInfo, buffer).then(result => {
        console.log(`[EasyModeCapture] Flushed ${result.count} on disable`);
      });
    }
  }

  // Cleanup
  actionBuffer = null;
  sessionInfo = null;

  if (appStateListener) {
    appStateListener();
    appStateListener = null;
  }

  console.log('[EasyModeCapture] Capture disabled');
}

/**
 * Flush remaining buffer to backend.
 */
export async function flushBuffer(): Promise<{ count: number }> {
  if (!actionBuffer || actionBuffer.size === 0 || !sessionInfo) return { count: 0 };

  const buffer = actionBuffer.flush();
  const result = await syncToBackend(sessionInfo, buffer);
  return result;
}

/**
 * Flush on visibility change — call from visibilitychange listener.
 */
export async function flushOnVisibility(): Promise<{ count: number }> {
  return flushBuffer();
}

/**
 * Get session info from AsyncStorage.
 */
export async function getSessionInfo(): Promise<SessionInfo | null> {
  const stored = await AsyncStorage.getItem(LOCAL_SESSION_KEY);
  return stored ? JSON.parse(stored) : null;
}

// ============================================================================
// AppState Listener (visibility flush)
// ============================================================================

function handleAppStateChange(appState: AppStateStatus): void {
  if (appState === 'background' && captureEnabled) {
    flushBuffer();
  }
}

// Export the middleware for persistState.tsx
export function captureMiddleware(store: any): any {
  return (next: any) => (action: any) => {
    const result = next(action);

    // Only process if capture is enabled + has buffer
    if (captureEnabled && actionBuffer && action?.type) {
      try {
        actionBuffer.push(action);
      } catch { /* safe guard — never let capture break the app */ }
    }

    return result;
  };
}
